import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../api';
import {
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Paper, Typography,
  Chip, IconButton, MenuItem, Select, TextField, Box, Card, useMediaQuery, useTheme,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CancelIcon from '@mui/icons-material/Cancel';
import Tooltip from '@mui/material/Tooltip';

const ESTADO_COLOR = {
  pendiente: 'warning',
  confirmada: 'info',
  completada: 'success',
  cancelada: 'default',
};

const CitasDashboard = () => {
  const api = useApi();
  const [citas, setCitas] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroFecha, setFiltroFecha] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchCitas = useCallback(async () => {
    try {
      const params = {};
      if (filtroEstado) params.estado = filtroEstado;
      if (filtroFecha) params.fecha = filtroFecha;
      const res = await api.get(`${process.env.REACT_APP_API_URL}/api/citas/`, { params });
      setCitas(res.data);
    } catch (err) {
      console.error('Error al cargar citas', err);
    }
  }, [api, filtroEstado, filtroFecha]);

  useEffect(() => {
    fetchCitas();
  }, [fetchCitas]);

  const cambiarEstado = async (cita, estado) => {
    try {
      await api.patch(
        `${process.env.REACT_APP_API_URL}/api/citas/${cita.id}/cambiar-estado/`,
        { estado }
      );
      fetchCitas();
    } catch (err) {
      console.error('Error al cambiar estado de la cita', err);
    }
  };

  return (
    <Paper sx={{ padding: 2 }}>
      <div
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 20, flexWrap: 'wrap', gap: 12,
        }}
      >
        <Typography className="titulo-app" variant="h5" gutterBottom>
          <EventIcon sx={{ mr: 1, fontSize: 32, color: '#083a94', verticalAlign: 'middle' }} />
          Citas agendadas
        </Typography>

        <div style={{ display: 'flex', gap: 12 }}>
          <Select
            size="small"
            displayEmpty
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
          >
            <MenuItem value="">Todas</MenuItem>
            <MenuItem value="pendiente">Pendiente</MenuItem>
            <MenuItem value="confirmada">Confirmada</MenuItem>
            <MenuItem value="completada">Completada</MenuItem>
            <MenuItem value="cancelada">Cancelada</MenuItem>
          </Select>
          <TextField
            type="date"
            size="small"
            value={filtroFecha}
            onChange={(e) => setFiltroFecha(e.target.value)}
          />
        </div>
      </div>

      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {citas.map((cita) => (
            <Card key={cita.id} variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography sx={{ fontWeight: 700 }}>{cita.fecha} · {cita.hora?.slice(0, 5)}</Typography>
                <Chip label={cita.estado} color={ESTADO_COLOR[cita.estado] || 'default'} size="small" />
              </Box>
              <Typography variant="body2" color="text.secondary">{cita.nombre_cliente} · {cita.telefono}</Typography>
              <Typography variant="body2" color="text.secondary">{cita.placa} {cita.marca_modelo}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{cita.motivo}</Typography>
              {cita.estado !== 'cancelada' && cita.estado !== 'completada' && (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {cita.estado === 'pendiente' && (
                    <Tooltip title="Confirmar" arrow>
                      <IconButton color="info" onClick={() => cambiarEstado(cita, 'confirmada')}>
                        <CheckCircleIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Marcar completada" arrow>
                    <IconButton color="success" onClick={() => cambiarEstado(cita, 'completada')}>
                      <DoneAllIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Cancelar" arrow>
                    <IconButton color="error" onClick={() => cambiarEstado(cita, 'cancelada')}>
                      <CancelIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Card>
          ))}
        </Box>
      ) : (
      <TableContainer sx={{ overflowX: 'auto' }}>
      <Table className="tabla-app">
        <TableHead>
          <TableRow>
            <TableCell>Fecha</TableCell>
            <TableCell>Hora</TableCell>
            <TableCell>Cliente</TableCell>
            <TableCell>Teléfono</TableCell>
            <TableCell>Vehículo</TableCell>
            <TableCell>Motivo</TableCell>
            <TableCell>Estado</TableCell>
            <TableCell>Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {citas.map((cita) => (
            <TableRow key={cita.id}>
              <TableCell>{cita.fecha}</TableCell>
              <TableCell>{cita.hora?.slice(0, 5)}</TableCell>
              <TableCell>{cita.nombre_cliente}</TableCell>
              <TableCell>{cita.telefono}</TableCell>
              <TableCell>{cita.placa} {cita.marca_modelo}</TableCell>
              <TableCell>{cita.motivo}</TableCell>
              <TableCell>
                <Chip label={cita.estado} color={ESTADO_COLOR[cita.estado] || 'default'} size="small" />
              </TableCell>
              <TableCell>
                {cita.estado !== 'cancelada' && cita.estado !== 'completada' && (
                  <>
                    {cita.estado === 'pendiente' && (
                      <Tooltip title="Confirmar" arrow>
                        <IconButton color="info" onClick={() => cambiarEstado(cita, 'confirmada')}>
                          <CheckCircleIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Marcar completada" arrow>
                      <IconButton color="success" onClick={() => cambiarEstado(cita, 'completada')}>
                        <DoneAllIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancelar" arrow>
                      <IconButton color="error" onClick={() => cambiarEstado(cita, 'cancelada')}>
                        <CancelIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </TableContainer>
      )}
    </Paper>
  );
};

export default CitasDashboard;
